import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { QueryConnectionsEnum } from "@/enum/queryStatus";
import { QueryConnectionsSchema } from "../data/schema";
import { useCreateConnectionMutation } from "@/services/it/updates/query-connections";
import { toast } from "components/ui/use-toast";
import { MESSAGES } from "../utils/messages";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";

type ConnectionData = z.infer<typeof QueryConnectionsSchema>;

export function CreateConnectionModal({}) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ConnectionData>({
    resolver: zodResolver(QueryConnectionsSchema),
  });

  const { handleModal } = useDialogModal();

  const [updateConnection, { isLoading }] = useCreateConnectionMutation();

  const onSubmit = async ({
    id = 0,
    connection_status,
    connection_name = "",
    connection_nickname = "",
    connection_url = "",
    connection_user = "",
    connection_pass = "",
    connection_db = "",
    connection_port = 0,
    connection_host = "",
    connection_type = "",
    select_allowed = false,
    updates_allowed = false,
  }: ConnectionData) => {
    try {
      await updateConnection({
        id,
        connection_status: connection_status as QueryConnectionsEnum,
        connection_name,
        connection_nickname,
        connection_url,
        connection_user,
        connection_pass,
        connection_db,
        connection_port,
        connection_host,
        connection_type,
        select_allowed,
        updates_allowed,
      }).unwrap();

      toast(MESSAGES.CREATE.SUCCESS);
      handleModal({
        isOpen: false,
      });
    } catch (error) {
      console.error("Failed to update connection:", error);
      toast(MESSAGES.CREATE.ERROR);
    }
  };

  return (
    <div className="sm:min-w-[600px] md:min-w-[700px] min:max-w-[800px] max-h-[80vh] bg-white overflow-auto">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 w-full">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-4">
            <div>
              <Label
                htmlFor="connection_nickname"
                className="text-sm font-medium text-muted-foreground"
              >
                Apelido (Opcional)
              </Label>
              <Input
                id="connection_nickname"
                {...register("connection_nickname")}
                className={cn(
                  "w-full mt-1",
                  errors.connection_nickname && "border-red-500"
                )}
              />
            </div>
            <div>
              <Label
                htmlFor="connection_name"
                className="text-sm font-medium text-muted-foreground"
              >
                Nome do Serviço (Service Name)
              </Label>
              <Input
                id="connection_name"
                {...register("connection_name")}
                className={cn(
                  "w-full mt-1",
                  errors.connection_name && "border-red-500"
                )}
              />
            </div>
            <div>
              <Label
                htmlFor="connection_url"
                className="text-sm font-medium text-muted-foreground"
              >
                URL
              </Label>
              <Input
                id="connection_url"
                {...register("connection_url")}
                className={cn(
                  "w-full mt-1",
                  errors.connection_url && "border-red-500"
                )}
              />
            </div>
            <div>
              <Label
                htmlFor="connection_user"
                className="text-sm font-medium text-muted-foreground"
              >
                User
              </Label>
              <Input
                id="connection_user"
                {...register("connection_user")}
                className={cn(
                  "w-full mt-1",
                  errors.connection_user && "border-red-500"
                )}
              />
            </div>
            <div>
              <Label
                htmlFor="connection_pass"
                className="text-sm font-medium text-muted-foreground"
              >
                Password
              </Label>
              <Input
                id="connection_pass"
                type="password"
                {...register("connection_pass")}
                className={cn(
                  "w-full mt-1",
                  errors.connection_pass && "border-red-500"
                )}
              />
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label
                htmlFor="connection_db"
                className="text-sm font-medium text-muted-foreground"
              >
                Database
              </Label>
              <Input
                id="connection_db"
                {...register("connection_db")}
                className={cn(
                  "w-full mt-1",
                  errors.connection_db && "border-red-500"
                )}
              />
            </div>
            <div>
              <Label
                htmlFor="connection_port"
                className="text-sm font-medium text-muted-foreground"
              >
                Port
              </Label>
              <Input
                id="connection_port"
                type="number"
                {...register("connection_port", { valueAsNumber: true })}
                className={cn(
                  "w-full mt-1",
                  errors.connection_port && "border-red-500"
                )}
              />
            </div>
            <div>
              <Label
                htmlFor="connection_host"
                className="text-sm font-medium text-muted-foreground"
              >
                Host
              </Label>
              <Input
                id="connection_host"
                {...register("connection_host")}
                className={cn(
                  "w-full mt-1",
                  errors.connection_host && "border-red-500"
                )}
              />
            </div>
            <div>
              <Label
                htmlFor="connection_type"
                className="text-sm font-medium text-muted-foreground"
              >
                Type
              </Label>
              <Input
                id="connection_type"
                {...register("connection_type")}
                className={cn(
                  "w-full mt-1",
                  errors.connection_type && "border-red-500"
                )}
              />
            </div>
            <div>
              <Label
                htmlFor="connection_status"
                className="text-sm font-medium text-muted-foreground"
              >
                Status
              </Label>
              <Controller
                name="connection_status"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue placeholder="Selecionar Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={QueryConnectionsEnum.ENABLED}>
                        Ativo
                      </SelectItem>
                      <SelectItem value={QueryConnectionsEnum.DISABLED}>
                        Desativado
                      </SelectItem>
                      <SelectItem value={QueryConnectionsEnum.PAUSED}>
                        Pausado
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:space-y-0 border p-4 rounded-md">
          <div className="flex items-center space-x-2">
            <Controller
              name="select_allowed"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label
              htmlFor="select_allowed"
              className="text-sm font-medium text-muted-foreground"
            >
              Permitir Consultas via Selects
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Controller
              name="updates_allowed"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label
              htmlFor="updates_allowed"
              className="text-sm font-medium text-muted-foreground"
            >
              Permitir Consultas de Alteração
            </Label>
          </div>
        </div>
        {Object.values(errors).length > 0 && (
          <>
            <div className="text-red-500 text-sm">
              Corrija os erros acima antes de enviar.
            </div>
          </>
        )}
      </form>
      <DialogFooter className="my-2">
        <Button
          variant="outline"
          onClick={() =>
            handleModal({
              isOpen: false,
            })
          }
          className="rounded-md"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading}
          className="bg-[#FFCC32] text-black rounded-md font-semibold text-sm p-2 px-4 flex items-center gap-2 hover:[#FFCC32]/80 border-none"
        >
          {isLoading ? "Carregando" : "Salvar Alterações"}
        </Button>
      </DialogFooter>
    </div>
  );
}
